import { useTranslation } from "react-i18next";
import {
  AutoComplete,
  FormContainer,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { IPriceInsuranceProps } from "./models";
import { insuranceRegisterList } from "../../../../../../../services/ticketAutomationReceivedRuleInsurance.service";
import {
  priceInsuranceSchema,
  TPriceInsuranceForm,
} from "./priceInsuranceSchema";
import {
  createTicketAutomationReceivedRulePriceInsurance,
  getTicketAutomationReceivedRulePriceInsuranceById,
  updateTicketAutomationReceivedRulePriceInsuranceById,
} from "../../../../../../../services/ticketAutomationReceivedRulePriceInsurance.service";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.automationReceivedRulePriceInsurance.";

const PriceInsurance = ({
  id,
  ticketAutomationReceivedRuleId,
  onBackButton,
}: IPriceInsuranceProps) => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createTicketAutomationReceivedRulePriceInsurance,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateTicketAutomationReceivedRulePriceInsuranceById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getTicketAutomationReceivedRulePriceInsuranceById,
  );

  const updateHandler = (v: TPriceInsuranceForm, id: string) =>
    update({
      item: {
        id: id,
        ...v,
        externalName: v.insuranceRegister.name,
        externalId: v.insuranceRegister.id,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
      },
      id: id,
    });

  const saveHandler = (v: TPriceInsuranceForm) =>
    save({
      ...v,
      externalName: v.insuranceRegister.name,
      externalId: v.insuranceRegister.id,
      ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TPriceInsuranceForm>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TPriceInsuranceForm = {
          ...data,
          insuranceRegister: {
            id: data.externalId,
            name: data.externalName,
          },
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const [
    optionsTicketAutomationReceivedSlotCalendars,
    setTicketAutomationReceivedSlotCalendars,
  ] = useState<ISelectType[]>([]);

  const { sendRequest } = useFetch(insuranceRegisterList);

  const params = useMemo(() => {
    return {
      EnableCode: true,
    };
  }, []);

  const fetchTicketAutomationReceivedSlotCalendars = useCallback(async () => {
    const { data, success } = await sendRequest(params);
    if (data && success) {
      setTicketAutomationReceivedSlotCalendars(
        data.map((d) => ({
          id: d.identifier.toString(),
          name: d.insuranceName,
        })),
      );
    }
  }, [sendRequest, params]);

  useEffect(() => {
    fetchTicketAutomationReceivedSlotCalendars();
  }, [fetchTicketAutomationReceivedSlotCalendars]);

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TPriceInsuranceForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "insurance")}
      schema={priceInsuranceSchema}
      subtitle={t(translationPath + "new_insurance")}
      backButtonOnClick={onBackButton}
      redirectOnSave={false}
      onSuccess={onBackButton}
      isModalForm={true}
      propId={id}
      formActionsOnBottom={false}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <AutoComplete
              fullWidth
              options={optionsTicketAutomationReceivedSlotCalendars}
              name="insuranceRegister"
              label={t(translationPath + "insurance_field")}
              getOptionLabel={(option) => option.name}
              size="small"
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default PriceInsurance;
